import React, { useEffect } from 'react'
import ClientInformation from './ClientInformation'

// Step 1 of 3 — "Invoice Recipient". Matches the Scale Portal popup screenshot:
//   - Two radio cards: "Send to all parties" / "Send to one party"
//   - Per-party detail cards (plaintiff side / defendant side or single)
//   - Inline-edit pencils on each card via ClientInformation
//
// Selected radio card uses a teal-gradient background; unselected uses a
// neutral border-gray card.
export default function InvoiceFirstStep({
    values,
    setFieldValue,
    plaintiff,
    defendant,
    additionalPlaintiffParties = [],
    additionalDefendantParties = [],
    isGscCase = false,
    onClientChange,
}) {
    const selectedOption = values.selectedOption || (isGscCase ? 'one' : 'all')
    const selectedParty = values.selectedParty

    // GSC cases: force single-party mode + auto-select the GSC client.
    useEffect(() => {
        if (isGscCase) {
            const gscId = values.clientForGscCase?.client_id || plaintiff?.client_id || defendant?.client_id
            if (selectedOption !== 'one') setFieldValue('selectedOption', 'one')
            if (gscId && selectedParty !== gscId) setFieldValue('selectedParty', gscId)
        }
    // eslint-disable-next-line
    }, [isGscCase])

    const allClients = [
        plaintiff,
        ...additionalPlaintiffParties.map((p) => ({ client_id: p.client_id, name: p.name, email: p.email, phone: p.phone, role: 'plaintiff' })),
        defendant,
        ...additionalDefendantParties.map((p) => ({ client_id: p.client_id, name: p.name, email: p.email, phone: p.phone, role: 'defendant' })),
    ].filter((c) => c && c.client_id)

    const setOption = (opt) => {
        setFieldValue('selectedOption', opt)
        if (opt === 'all') setFieldValue('selectedParty', null)
    }

    const isSelected = (opt) => selectedOption === opt

    const cardCls = (sel) =>
        `border rounded-lg p-3 cursor-pointer transition-colors ${
            sel
                ? 'border-transparent text-white'
                : 'border-gray-200 hover:border-gray-300 bg-white'
        }`

    const cardStyle = (sel) =>
        sel
            ? { background: 'linear-gradient(90deg, #115E59 0%, #0F766E 100%)' }
            : {}

    return (
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div>
                <h3 className="text-base font-semibold text-gray-900">Invoice Recipient</h3>
                <p className="text-sm text-gray-500 mt-0.5">Select who will receive this invoice.</p>
            </div>

            {!isGscCase && (
                <div className="space-y-4">
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setOption('all')}
                        className={cardCls(isSelected('all'))}
                        style={cardStyle(isSelected('all'))}
                    >
                        <div className="flex items-start gap-3">
                            <span className={`mt-0.5 w-4 h-4 rounded-full border ${isSelected('all') ? 'border-white' : 'border-gray-400'} flex items-center justify-center shrink-0`}>
                                {isSelected('all') && <span className="w-2 h-2 rounded-full bg-white" />}
                            </span>
                            <div>
                                <div className={`font-bold text-[15px] ${isSelected('all') ? 'text-white' : 'text-gray-900'}`}>Send to all parties</div>
                                <div className={`text-[15px] mt-0.5 ${isSelected('all') ? 'text-white/90' : 'text-gray-500'}`}>The invoice will be sent to all parties involved in the mediation</div>
                            </div>
                        </div>
                    </div>

                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setOption('one')}
                        className={cardCls(isSelected('one'))}
                        style={cardStyle(isSelected('one'))}
                    >
                        <div className="flex items-start gap-3">
                            <span className={`mt-0.5 w-4 h-4 rounded-full border ${isSelected('one') ? 'border-white' : 'border-gray-400'} flex items-center justify-center shrink-0`}>
                                {isSelected('one') && <span className="w-2 h-2 rounded-full bg-white" />}
                            </span>
                            <div className="flex-1">
                                <div className={`font-bold text-[15px] ${isSelected('one') ? 'text-white' : 'text-gray-900'}`}>Send to one party</div>
                                <div className={`text-[15px] mt-0.5 ${isSelected('one') ? 'text-white/90' : 'text-gray-500'}`}>Select a specific party to receive the invoice</div>
                                {isSelected('one') && (
                                    <select
                                        value={selectedParty || ''}
                                        onChange={(e) => setFieldValue('selectedParty', e.target.value ? Number(e.target.value) : null)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="mt-2 w-full text-sm border border-gray-300 bg-white text-gray-900 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-700/30"
                                    >
                                        <option value="">Please Select</option>
                                        {allClients.map((c) => {
                                            const role = c.role
                                                ? c.role.charAt(0).toUpperCase() + c.role.slice(1)
                                                : ''
                                            return (
                                                <option key={c.client_id} value={c.client_id}>
                                                    {c.name}{role ? ` - ${role}` : ''}
                                                </option>
                                            )
                                        })}
                                    </select>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Per-party cards below — full-width single card for "one", split 6/6 for "all" */}
            <div className="py-4">
                {selectedOption === 'one' && selectedParty ? (
                    <div className="border border-gray-100 rounded-md p-4 bg-white">
                        <ClientInformation
                            client={allClients.find((c) => c.client_id === selectedParty)}
                            label="Recipient"
                            onClientChange={onClientChange}
                        />
                    </div>
                ) : (
                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-12 md:col-span-6 space-y-2">
                            <ClientInformation client={plaintiff} label="Plaintiff" onClientChange={onClientChange} />
                            {additionalPlaintiffParties.map((p, idx) => (
                                <ClientInformation key={p.id || idx} client={{ client_id: p.client_id, name: p.name, email: p.email, phone: p.phone }} label={`Plaintiff Party ${idx + 2}`} onClientChange={onClientChange} />
                            ))}
                        </div>
                        <div className="col-span-12 md:col-span-6 space-y-2">
                            <ClientInformation client={defendant} label="Defendant" onClientChange={onClientChange} />
                            {additionalDefendantParties.map((p, idx) => (
                                <ClientInformation key={p.id || idx} client={{ client_id: p.client_id, name: p.name, email: p.email, phone: p.phone }} label={`Defendant Party ${idx + 2}`} onClientChange={onClientChange} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
